import { useAuth } from '@contexts/auth-provider';
import { useMsalAuth } from '@contexts/msal-provider';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type {
  SalesforceRedirectState,
  UseSalesforceRedirectReturn,
} from './useSalesforceRedirect.types';

import { useImportSfAccount } from '../use-import-sf-account/useImportSfAccount';
import { useOrgSelection } from '../use-org-selection/useOrgSelection';
import { usePermissionsCheck } from '../use-permissions-check/usePermissionsCheck';
import { useSfAccount } from '../use-sf-account/useSfAccount';
import { useSfContact } from '../use-sf-contact/useSfContact';
import { useSfNavigation } from '../use-sf-navigation/useSfNavigation';
import { useSyncUser } from '../use-sync-user/useSyncUser';
import {
  computeSalesforceRedirectState,
  parseSalesforceParams,
} from './useSalesforceRedirect.utils';

/**
 * Orchestration hook for the Salesforce redirect flow.
 *
 * Composes focused sub-hooks for each pipeline stage:
 *   usePermissionsCheck → useSfContact → useSfAccount → useOrgSelection → useSfNavigation
 *
 * Contact GET fires in parallel with permissions. Org GET is deferred until
 * discrepancy is None or resolved via sync.
 */
export function useSalesforceRedirect(): UseSalesforceRedirectReturn {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { login } = useMsalAuth();
  const [searchParams] = useSearchParams();

  const params = useMemo(
    () => (isAuthenticated ? parseSalesforceParams(searchParams) : null),
    [isAuthenticated, searchParams]
  );

  const loginTriggered = useRef(false);
  const [authLoginFailed, setAuthLoginFailed] = useState(false);

  useEffect(() => {
    if (
      !isAuthLoading &&
      !isAuthenticated &&
      !authLoginFailed &&
      !loginTriggered.current
    ) {
      loginTriggered.current = true;
      void login('b2e').catch((error: unknown) => {
        console.error('Salesforce login failed:', error);
        setAuthLoginFailed(true);
      });
    }
  }, [isAuthLoading, isAuthenticated, authLoginFailed, login]);

  const [discrepancyResolved, setDiscrepancyResolved] = useState(false);

  const permissionsGranted = usePermissionsCheck(isAuthenticated, !!params);

  const contactQuery = useSfContact(
    params?.sfMasterContactId ?? '',
    params?.sfAccountId ?? ''
  );

  const shouldFetchOrg =
    !!contactQuery.data &&
    (contactQuery.data.discrepancyType === 'None' || discrepancyResolved);
  const orgQuery = useSfAccount(
    shouldFetchOrg ? (params?.sfAccountId ?? '') : ''
  );

  const importMutation = useImportSfAccount();
  const {
    isPending: isImportPending,
    isSuccess: isImportSuccess,
    isError: isImportError,
    mutate: importMutate,
    reset: importReset,
  } = importMutation;

  const syncMutation = useSyncUser(
    contactQuery.data?.caeConnectContact?.id ?? '',
    (): void => {
      setDiscrepancyResolved(true);
    }
  );

  useNavigationBlock(syncMutation.isPending || isImportPending);

  const { orgReady, orgError, resetOrgError } = useOrgSelection(
    orgQuery.data,
    permissionsGranted
  );

  useSfNavigation(orgReady, orgQuery.data, contactQuery.data);

  const state = useMemo(
    (): SalesforceRedirectState =>
      authLoginFailed
        ? { step: 'load-error' }
        : computeSalesforceRedirectState({
            isAuthLoading,
            isAuthenticated,
            params,
            permissionsGranted,
            contactData: contactQuery.data,
            contactIsError: contactQuery.isError,
            discrepancyResolved,
            isSyncing: syncMutation.isPending,
            orgData: orgQuery.data,
            orgIsError: orgQuery.isError,
            isImportPending,
            isImportSuccess,
            isImportError,
            orgError,
          }),
    [
      isAuthLoading,
      authLoginFailed,
      isAuthenticated,
      params,
      permissionsGranted,
      contactQuery.data,
      contactQuery.isError,
      discrepancyResolved,
      syncMutation.isPending,
      orgQuery.data,
      orgQuery.isError,
      isImportPending,
      isImportSuccess,
      isImportError,
      orgError,
    ]
  );

  const importOrganization = useCallback((): void => {
    if (params) importMutate(params.sfAccountId);
  }, [params, importMutate]);

  const handleSyncUser = useCallback((): void => {
    syncMutation.mutate();
  }, [syncMutation]);

  const retry = useCallback((): void => {
    if (authLoginFailed) {
      loginTriggered.current = false;
      setAuthLoginFailed(false);
    } else if (contactQuery.isError) {
      contactQuery.refetch();
    } else if (orgQuery.isError) {
      orgQuery.refetch();
    } else if (orgError) {
      resetOrgError();
    } else if (isImportError && params) {
      importReset();
      importMutate(params.sfAccountId);
    }
  }, [
    authLoginFailed,
    contactQuery,
    orgQuery,
    orgError,
    resetOrgError,
    isImportError,
    importReset,
    importMutate,
    params,
  ]);

  return { state, importOrganization, syncUser: handleSyncUser, retry };
}
